import React from 'react';
import Modal from 'components/ModalPopup';
import { Button, Group, Select, TextInput } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { moduleManagementService } from 'services/dacum/module.service';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const formSchema = z.object({
  TrCompetencyModuleID: z.string().min(1, 'Module Competency is required'),
  JobFamilyID: z
    .string()
    .min(1, 'Job Family ID is required')
    .max(20, 'Job Family ID must be 20 characters or less')
});

type FormData = z.infer<typeof formSchema>;

interface CreateModuleJobFamilyProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (_data: FormData) => void;
}

const CreateModuleJobFamily: React.FC<CreateModuleJobFamilyProps> = ({
  opened,
  onClose,
  onSubmit
}) => {
  const { data, isLoading } = useQuery({
    queryKey: ['moduleCompetency'],
    queryFn: () =>
      moduleManagementService.getModuleCompetency({
        sortOrder: 'asc',
        pageNumber: 1,
        pageSize: 1000
      })
  });

  const {
    control,
    handleSubmit: handleFormSubmit,
    formState: { errors },
    reset
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      TrCompetencyModuleID: '',
      JobFamilyID: ''
    }
  });

  const handleSubmit = (formData: FormData) => {
    onSubmit(formData);
    reset();
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };
  return (
    <Modal
      title='Create Module Job Family'
      size='lg'
      opened={opened}
      close={handleClose}
      withCloseButton
    >
      <form onSubmit={handleFormSubmit(handleSubmit)}>
        <Controller
          name='TrCompetencyModuleID'
          control={control}
          render={({ field }) => (
            <Select
              label='Module Competency'
              placeholder='Select Module Competency'
              data={
                data?.data.map((item) => ({
                  label: item.courseName,
                  value: item.trCompetencyModuleID
                })) || []
              }
              value={field.value}
              onChange={(value) => field.onChange(value || '')}
              disabled={isLoading}
              error={errors.TrCompetencyModuleID?.message}
              mb={16}
            />
          )}
        />
        <Controller
          name='JobFamilyID'
          control={control}
          render={({ field }) => (
            <TextInput
              label='Job Family ID'
              placeholder='Input job family ID'
              value={field.value}
              onChange={field.onChange}
              error={errors.JobFamilyID?.message}
              mb={24}
            />
          )}
        />
        <Group justify='flex-end'>
          <Button variant='outline' onClick={handleClose}>
            Cancel
          </Button>
          <Button type='submit'>Submit</Button>
        </Group>
      </form>
    </Modal>
  );
};

export default CreateModuleJobFamily;
